'use client'

import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { messagingInteractionEvent } from '@marketplace-web/messaging/tracking-data'

type Props = Omit<Parameters<typeof messagingInteractionEvent>[0], 'duration' | 'action'>

export default memo(function TimeOnTaskTracker({ screen, conversationId }: Props) {
  const [startTime, setStartTime] = useState(() => Date.now())
  const { track } = useTracking()
  const trackTimeOnTaskRef = useRef<(() => void) | null>(null)

  const resetStartTime = useCallback(() => setStartTime(Date.now()), [])

  const trackTimeOnTask = useCallback(() => {
    const duration = Date.now() - startTime

    track(
      messagingInteractionEvent({
        screen,
        conversationId,
        duration,
        action: 'time_on_task_in_ms',
      }),
    )
  }, [screen, track, startTime, conversationId])

  useEffect(() => {
    trackTimeOnTaskRef.current = trackTimeOnTask
  }, [trackTimeOnTask])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackTimeOnTaskRef.current?.()

        return
      }

      resetStartTime()
    }

    const handleBeforeUnload = () => trackTimeOnTaskRef.current?.()

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)

      trackTimeOnTaskRef.current?.()
    }
  }, [resetStartTime])

  return null
})
